"use client";
import React, { FC } from "react";
import Image from "next/image";
import Link from "next/link";
import { useParams } from "next/navigation";
import { projectsData } from "../../../lib/fackData/projectsData";
import ProjectImageSlider from "./ProjectImageSlider";

interface ProjectDetailProps {
  projectSlug?: string;
}

const ProjectDetail: FC<ProjectDetailProps> = ({ projectSlug }) => {
  const params = useParams();
  const slug = projectSlug || (params?.slug as string);
  
  const project = slug ? projectsData.find(p => p.slug === slug) : null;

  if (!project) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <p className="text-gray-600">Projekt nicht gefunden</p>
        <Link href="/projects" className="mt-4 inline-block text-company-color-primary hover:underline">
          Zurück zur Projektübersicht
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative h-[50vh] min-h-[400px] overflow-hidden">
        <Image
          src={project.project_img}
          alt={project.project_name}
          fill
          className="object-cover"
          priority
        />
        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
          <div className="text-center text-white px-4">
            <h1 className="text-4xl md:text-6xl font-bold mb-4">{project.project_name}</h1>
            <p className="text-xl md:text-2xl">{project.location}</p>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="container mx-auto px-4 py-16 max-w-6xl">
        <div className="grid md:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="md:col-span-2">
            <h2 className="text-3xl font-bold text-company-color-primary mb-6">Projektbeschreibung</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              {project.project_desc}
            </p>
            
            {/* Image Slider */}
            {project.images && project.images.length > 0 && (
              <div className="mt-12">
                <ProjectImageSlider 
                  images={project.images} 
                  projectName={project.project_name}
                />
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-8">
              <h3 className="text-2xl font-bold text-company-color-primary mb-6">Projektdetails</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-gray-500 uppercase mb-1">Kunde</p>
                  <p className="text-lg text-gray-900">{project.client}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-500 uppercase mb-1">Projektjahr</p>
                  <p className="text-lg text-gray-900">{project.project_year}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-500 uppercase mb-1">Projekttyp</p>
                  <p className="text-lg text-gray-900">{project.project_type}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-500 uppercase mb-1">Fläche</p>
                  <p className="text-lg text-gray-900">{project.area}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-500 uppercase mb-1">Standort</p>
                  <p className="text-lg text-gray-900">{project.location}</p>
                </div>
              </div>
              
              <Link
                href="/projects"
                className="mt-8 block w-full text-center px-6 py-3 bg-company-color-primary text-white font-semibold rounded hover:bg-opacity-90 transition-colors duration-200"
              >
                Zurück zur Übersicht
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
